/**
 * Comprehensive Cookie Management System for Tennis Gourmet
 * 
 * This module provides a unified interface for managing all cookies in the application,
 * replacing the inconsistent localStorage + cookie pattern with a standardized approach.
 * 
 * Features:
 * - Granular cookie categories (Essential, Analytics, Marketing)
 * - Proper cookie deletion and cleanup
 * - GDPR compliance utilities
 * - Consent expiry management
 * - Audit logging for compliance
 */

// Cookie categories for granular consent management
export enum CookieCategory {
  ESSENTIAL = 'essential',
  ANALYTICS = 'analytics',
  MARKETING = 'marketing'
}

// Cookie consent state interface
export interface CookieConsent {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
  version: string;
  expiresAt: number;
}

// Individual cookie information for transparency
export interface CookieInfo {
  name: string;
  category: CookieCategory;
  purpose: string;
  duration: string;
  provider: string;
  essential: boolean;
}

// Cookie audit log entry
export interface CookieAuditEntry {
  timestamp: number;
  action: 'consent_given' | 'consent_revoked' | 'consent_updated' | 'cookies_cleared';
  categories: Partial<CookieConsent>;
  userAgent: string;
  ipHash?: string; // For compliance logging (hashed for privacy)
}

class CookieManager {
  private readonly CONSENT_COOKIE_NAME = 'tg_cookie_consent';
  private readonly AUDIT_STORAGE_KEY = 'tg_cookie_audit';
  private readonly CONSENT_VERSION = '1.0';
  private readonly CONSENT_DURATION_DAYS = 365;

  // Cookie inventory for transparency
  private readonly cookieInventory: CookieInfo[] = [
    {
      name: 'tg_cookie_consent',
      category: CookieCategory.ESSENTIAL,
      purpose: 'Stores user cookie preferences and consent state',
      duration: '1 year',
      provider: 'Tennis Gourmet',
      essential: true
    },
    {
      name: '_ga',
      category: CookieCategory.ANALYTICS,
      purpose: 'Google Analytics - distinguishes unique users',
      duration: '2 years',
      provider: 'Google Analytics',
      essential: false
    },
    {
      name: '_ga_*',
      category: CookieCategory.ANALYTICS,
      purpose: 'Google Analytics 4 - session and campaign data',
      duration: '2 years',
      provider: 'Google Analytics',
      essential: false
    },
    {
      name: '_gid',
      category: CookieCategory.ANALYTICS,
      purpose: 'Google Analytics - distinguishes users within 24 hours',
      duration: '24 hours',
      provider: 'Google Analytics',
      essential: false
    },
    {
      name: '_gat_*',
      category: CookieCategory.ANALYTICS,
      purpose: 'Google Analytics - throttles request rate',
      duration: '1 minute',
      provider: 'Google Analytics',
      essential: false
    }
  ];

  /**
   * Get current cookie consent state
   */
  getConsent(): CookieConsent | null {
    if (typeof window === 'undefined') return null;

    const consentCookie = this.getCookie(this.CONSENT_COOKIE_NAME);
    if (!consentCookie) return null;

    try {
      const consent = JSON.parse(consentCookie) as CookieConsent;
      
      // Check if consent has expired
      if (Date.now() > consent.expiresAt) {
        this.clearConsent();
        return null;
      }

      return consent;
    } catch (error) {
      console.error('Failed to parse cookie consent:', error);
      this.clearConsent();
      return null;
    }
  }

  /**
   * Set cookie consent with granular categories
   */
  setConsent(categories: Partial<Pick<CookieConsent, 'analytics' | 'marketing'>>): void {
    if (typeof window === 'undefined') return;

    const consent: CookieConsent = {
      essential: true, // Always true - essential cookies cannot be disabled
      analytics: categories.analytics ?? false,
      marketing: categories.marketing ?? false,
      timestamp: Date.now(),
      version: this.CONSENT_VERSION,
      expiresAt: Date.now() + (this.CONSENT_DURATION_DAYS * 24 * 60 * 60 * 1000)
    };

    // Set the consent cookie
    this.setCookie(
      this.CONSENT_COOKIE_NAME,
      JSON.stringify(consent),
      this.CONSENT_DURATION_DAYS,
      {
        secure: window.location.protocol === 'https:',
        sameSite: 'Lax',
        path: '/'
      }
    );

    // Update Google Analytics consent mode
    this.updateGoogleAnalyticsConsent(consent.analytics, consent.marketing);

    // Clean up cookies for revoked categories
    this.cleanupRevokedCookies(consent);

    // Log the consent action
    this.logConsentAction('consent_given', consent);

    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', {
      detail: consent
    }));
  }

  /**
   * Update existing consent (for preference center)
   */
  updateConsent(updates: Partial<Pick<CookieConsent, 'analytics' | 'marketing'>>): void {
    const currentConsent = this.getConsent();
    if (!currentConsent) {
      // If no existing consent, treat as new consent
      this.setConsent(updates);
      return;
    }

    const updatedConsent: CookieConsent = {
      ...currentConsent,
      ...updates,
      timestamp: Date.now() // Update timestamp
    };

    this.setCookie(
      this.CONSENT_COOKIE_NAME,
      JSON.stringify(updatedConsent),
      this.CONSENT_DURATION_DAYS,
      {
        secure: window.location.protocol === 'https:',
        sameSite: 'Lax',
        path: '/'
      }
    );

    // Update Google Analytics consent mode
    this.updateGoogleAnalyticsConsent(updatedConsent.analytics, updatedConsent.marketing);

    // Clean up cookies for newly revoked categories
    this.cleanupRevokedCookies(updatedConsent);

    // Log the update action
    this.logConsentAction('consent_updated', updatedConsent);

    // Dispatch event
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', {
      detail: updatedConsent
    }));
  }

  /**
   * Update Google Analytics consent mode
   */
  private updateGoogleAnalyticsConsent(analyticsConsent: boolean, marketingConsent: boolean): void {
    if (typeof window !== 'undefined' && window.gtag) {
      try {
        window.gtag('consent', 'update', {
          'analytics_storage': analyticsConsent ? 'granted' : 'denied',
          'ad_storage': marketingConsent ? 'granted' : 'denied',
          'ad_user_data': marketingConsent ? 'granted' : 'denied',
          'ad_personalization': marketingConsent ? 'granted' : 'denied'
        });
      } catch (error) {
        console.warn('Failed to update Google Analytics consent mode:', error);
      }
    }
  }

  /**
   * Clear all consent and cookies
   */
  clearConsent(): void {
    if (typeof window === 'undefined') return;

    // Remove consent cookie
    this.deleteCookie(this.CONSENT_COOKIE_NAME);

    // Clear all non-essential cookies
    this.clearAllNonEssentialCookies();

    // Log the action
    this.logConsentAction('consent_revoked', {
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
      version: this.CONSENT_VERSION,
      expiresAt: 0
    });

    // Dispatch event
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { 
      detail: null 
    }));
  }

  /**
   * Check if consent has been set
   */
  hasConsentBeenSet(): boolean {
    return this.getConsent() !== null;
  }

  /**
   * Check if specific category is consented
   */
  isCategoryConsented(category: CookieCategory): boolean {
    const consent = this.getConsent();
    if (!consent) return false;

    switch (category) {
      case CookieCategory.ESSENTIAL:
        return true; // Essential cookies are always allowed
      case CookieCategory.ANALYTICS:
        return consent.analytics;
      case CookieCategory.MARKETING:
        return consent.marketing;
      default:
        return false;
    }
  }

  /**
   * Get cookie inventory for transparency
   */
  getCookieInventory(): CookieInfo[] {
    return [...this.cookieInventory];
  }

  /**
   * Get cookies by category
   */
  getCookiesByCategory(category: CookieCategory): CookieInfo[] {
    return this.cookieInventory.filter(cookie => cookie.category === category);
  }

  /**
   * Clean up cookies for revoked categories
   */
  private cleanupRevokedCookies(consent: CookieConsent): void {
    // Clean up analytics cookies if analytics consent is revoked
    if (!consent.analytics) {
      this.clearGoogleAnalyticsCookies();
    }

    // Clean up marketing cookies if marketing consent is revoked
    if (!consent.marketing) {
      this.clearMarketingCookies();
    }

    // Note: Preferences are now handled via localStorage (no cookies needed)
  }

  /**
   * Clear all Google Analytics cookies and data
   */
  private clearGoogleAnalyticsCookies(): void {
    // Enhanced GA cookie patterns including GA4 and all variations
    const gaCookiePatterns = [
      '_ga', '_gid', '_gat', '_gtag', '_gcl_au', '_gcl_aw', '_gcl_dc',
      '_gac_', '_gat_gtag_', '_dc_gtm_', '_ga_G-', '_gat_UA-',
      '__utma', '__utmb', '__utmc', '__utmt', '__utmv', '__utmz', // Legacy GA
      '_fbp', '_fbc', 'fr', // Facebook Pixel (often used with GA)
      '_hjid', '_hjFirstSeen', '_hjIncludedInSessionSample', // Hotjar
      'AMP_TOKEN', '_gac_gb_', '_gcl_gb'
    ];

    // Update Google Analytics consent mode to denied
    if (typeof window !== 'undefined' && window.gtag) {
      try {
        window.gtag('consent', 'update', {
          'analytics_storage': 'denied',
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied'
        });
      } catch (error) {
        console.warn('Failed to update GA consent mode:', error);
      }
    }

    // Clear dataLayer
    if (typeof window !== 'undefined' && window.dataLayer) {
      try {
        // Clear existing dataLayer entries
        window.dataLayer.length = 0;
        // Re-initialize with consent denied
        window.dataLayer.push({
          'gtm.start': new Date().getTime(),
          event: 'gtm.js'
        });
      } catch (error) {
        console.warn('Failed to clear dataLayer:', error);
      }
    }

    // Get all cookies and find GA cookies
    const allCookies = document.cookie.split(';');

    // Define possible domains for cookie deletion
    const domains = [
      undefined, // Current domain
      window.location.hostname,
      '.' + window.location.hostname,
      '.google.com',
      '.googletagmanager.com',
      '.google-analytics.com',
      '.doubleclick.net'
    ];

    // Define possible paths
    const paths = ['/', '/ga', '/analytics'];

    allCookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();

      // Check if cookie matches GA patterns (more comprehensive)
      const isGACookie = gaCookiePatterns.some(pattern =>
        cookieName.startsWith(pattern) ||
        cookieName.includes(pattern) ||
        cookieName.match(new RegExp(`^${pattern.replace('*', '.*')}$`))
      );

      if (isGACookie) {
        // Try to delete with all possible domain and path combinations
        domains.forEach(domain => {
          paths.forEach(path => {
            this.deleteCookie(cookieName, path, domain);
          });
        });
      }
    });

    // Clear Google Analytics from localStorage and sessionStorage
    if (typeof Storage !== 'undefined') {
      // More comprehensive localStorage cleanup
      const storagePatterns = [
        '_ga', '_gid', '_gat', 'google', 'analytics', 'gtag', 'gtm',
        'firebase', 'amplitude', 'mixpanel', 'segment', 'hotjar'
      ];

      Object.keys(localStorage).forEach(key => {
        const shouldRemove = storagePatterns.some(pattern =>
          key.toLowerCase().includes(pattern.toLowerCase())
        );
        if (shouldRemove) {
          try {
            localStorage.removeItem(key);
          } catch (error) {
            console.warn(`Failed to remove localStorage key ${key}:`, error);
          }
        }
      });

      Object.keys(sessionStorage).forEach(key => {
        const shouldRemove = storagePatterns.some(pattern =>
          key.toLowerCase().includes(pattern.toLowerCase())
        );
        if (shouldRemove) {
          try {
            sessionStorage.removeItem(key);
          } catch (error) {
            console.warn(`Failed to remove sessionStorage key ${key}:`, error);
          }
        }
      });
    }

    // Clear IndexedDB data (GA4 sometimes uses this)
    if (typeof window !== 'undefined' && 'indexedDB' in window) {
      try {
        // Clear Firebase/GA4 IndexedDB
        const dbsToDelete = ['firebase-app-check-store', 'firebase-heartbeat-store'];
        dbsToDelete.forEach(dbName => {
          const deleteReq = indexedDB.deleteDatabase(dbName);
          deleteReq.onerror = () => console.warn(`Failed to delete IndexedDB: ${dbName}`);
        });
      } catch (error) {
        console.warn('Failed to clear IndexedDB:', error);
      }
    }
  }

  /**
   * Clear marketing cookies (placeholder for future marketing integrations)
   */
  private clearMarketingCookies(): void {
    // Placeholder for marketing cookie cleanup
    // Add specific marketing cookie patterns as needed
    const marketingPatterns = ['_fbp', '_fbc', 'fr']; // Facebook Pixel examples
    
    const allCookies = document.cookie.split(';');
    allCookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      const isMarketingCookie = marketingPatterns.some(pattern => 
        cookieName.startsWith(pattern)
      );
      
      if (isMarketingCookie) {
        this.deleteCookie(cookieName);
      }
    });
  }

  // Note: Preferences are now handled via localStorage (no cleanup needed)

  /**
   * Clear all non-essential cookies
   */
  private clearAllNonEssentialCookies(): void {
    this.clearGoogleAnalyticsCookies();
    this.clearMarketingCookies();
    // Note: Preferences are localStorage-only (no cookies to clear)
    
    this.logConsentAction('cookies_cleared', {
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
      version: this.CONSENT_VERSION,
      expiresAt: 0
    });
  }

  /**
   * Utility: Set cookie with options
   */
  private setCookie(
    name: string, 
    value: string, 
    days: number, 
    options: {
      secure?: boolean;
      sameSite?: 'Strict' | 'Lax' | 'None';
      path?: string;
      domain?: string;
    } = {}
  ): void {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
    let cookieString = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}`;
    
    if (options.path) cookieString += `; path=${options.path}`;
    if (options.domain) cookieString += `; domain=${options.domain}`;
    if (options.secure) cookieString += '; secure';
    if (options.sameSite) cookieString += `; samesite=${options.sameSite}`;
    
    document.cookie = cookieString;
  }

  /**
   * Utility: Get cookie value
   */
  private getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }
    return null;
  }

  /**
   * Utility: Delete cookie with comprehensive attribute handling
   */
  private deleteCookie(name: string, path: string = '/', domain?: string): void {
    if (typeof document === 'undefined') return;

    // Create multiple deletion strings to handle different cookie configurations
    const expireDate = 'Thu, 01 Jan 1970 00:00:00 UTC';
    const deletionStrings = [
      // Basic deletion
      `${name}=; expires=${expireDate}; path=${path}`,
      // With max-age (more reliable in some browsers)
      `${name}=; max-age=0; path=${path}`,
      // With secure flag
      `${name}=; expires=${expireDate}; path=${path}; secure`,
      `${name}=; max-age=0; path=${path}; secure`,
      // With SameSite variations
      `${name}=; expires=${expireDate}; path=${path}; samesite=lax`,
      `${name}=; expires=${expireDate}; path=${path}; samesite=strict`,
      `${name}=; expires=${expireDate}; path=${path}; samesite=none; secure`,
      `${name}=; max-age=0; path=${path}; samesite=lax`,
      `${name}=; max-age=0; path=${path}; samesite=strict`,
      `${name}=; max-age=0; path=${path}; samesite=none; secure`,
    ];

    // Add domain-specific variations if domain is provided
    if (domain) {
      const domainStrings = [
        `${name}=; expires=${expireDate}; path=${path}; domain=${domain}`,
        `${name}=; max-age=0; path=${path}; domain=${domain}`,
        `${name}=; expires=${expireDate}; path=${path}; domain=${domain}; secure`,
        `${name}=; max-age=0; path=${path}; domain=${domain}; secure`,
        `${name}=; expires=${expireDate}; path=${path}; domain=${domain}; samesite=lax`,
        `${name}=; expires=${expireDate}; path=${path}; domain=${domain}; samesite=strict`,
        `${name}=; expires=${expireDate}; path=${path}; domain=${domain}; samesite=none; secure`,
        `${name}=; max-age=0; path=${path}; domain=${domain}; samesite=lax`,
        `${name}=; max-age=0; path=${path}; domain=${domain}; samesite=strict`,
        `${name}=; max-age=0; path=${path}; domain=${domain}; samesite=none; secure`,
      ];
      deletionStrings.push(...domainStrings);
    }

    // Attempt deletion with all variations
    deletionStrings.forEach(cookieString => {
      try {
        document.cookie = cookieString;
      } catch (error) {
        // Silently continue if a particular deletion string fails
        console.debug(`Failed to delete cookie with string: ${cookieString}`, error);
      }
    });

    // Additional cleanup: try to delete with common domain variations
    if (!domain && typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const commonDomains = [
        hostname,
        '.' + hostname,
        hostname.split('.').slice(-2).join('.'), // Get main domain (e.g., example.com from sub.example.com)
        '.' + hostname.split('.').slice(-2).join('.')
      ];

      commonDomains.forEach(domainVariation => {
        this.deleteCookie(name, path, domainVariation);
      });
    }
  }

  /**
   * Log consent actions for audit trail
   */
  private logConsentAction(action: CookieAuditEntry['action'], consent: CookieConsent): void {
    if (typeof window === 'undefined' || typeof Storage === 'undefined') return;

    try {
      const auditEntry: CookieAuditEntry = {
        timestamp: Date.now(),
        action,
        categories: {
          essential: consent.essential,
          analytics: consent.analytics,
          marketing: consent.marketing
        },
        userAgent: navigator.userAgent
      };

      // Get existing audit log with corruption handling
      let auditLog: CookieAuditEntry[] = [];
      try {
        const existingLog = localStorage.getItem(this.AUDIT_STORAGE_KEY);
        auditLog = existingLog ? JSON.parse(existingLog) : [];

        // Validate that it's actually an array
        if (!Array.isArray(auditLog)) {
          console.warn('Audit log data is not an array, resetting to empty array');
          auditLog = [];
        }
      } catch (parseError) {
        console.warn('Corrupted audit log detected, clearing and starting fresh:', parseError);
        auditLog = [];
        // Clear the corrupted data
        localStorage.removeItem(this.AUDIT_STORAGE_KEY);
      }

      // Add new entry
      auditLog.push(auditEntry);

      // Implement proper FIFO: keep only the most recent 50 entries
      if (auditLog.length > 50) {
        auditLog = auditLog.slice(-50); // Keep the last 50 entries
      }

      // Save back to storage
      localStorage.setItem(this.AUDIT_STORAGE_KEY, JSON.stringify(auditLog));
    } catch (error) {
      console.error('Failed to log consent action:', error);

      // If storage fails, try to clear corrupted data and continue
      try {
        localStorage.removeItem(this.AUDIT_STORAGE_KEY);
      } catch (clearError) {
        // Storage might be completely unavailable, continue silently
      }
    }
  }

  /**
   * Get audit log for compliance
   */
  getAuditLog(): CookieAuditEntry[] {
    if (typeof window === 'undefined' || typeof Storage === 'undefined') return [];

    try {
      const existingLog = localStorage.getItem(this.AUDIT_STORAGE_KEY);
      if (!existingLog) return [];

      const auditLog = JSON.parse(existingLog);

      // Validate that it's actually an array
      if (!Array.isArray(auditLog)) {
        console.warn('Audit log data is not an array, clearing corrupted data');
        localStorage.removeItem(this.AUDIT_STORAGE_KEY);
        return [];
      }

      return auditLog;
    } catch (error) {
      console.warn('Failed to retrieve audit log due to corruption, clearing corrupted data:', error);

      // Clear corrupted data and return empty array
      try {
        localStorage.removeItem(this.AUDIT_STORAGE_KEY);
      } catch (clearError) {
        // Storage might be completely unavailable, continue silently
      }

      return [];
    }
  }

  /**
   * Clear audit log (for privacy/GDPR data deletion requests)
   */
  clearAuditLog(): void {
    if (typeof window === 'undefined' || typeof Storage === 'undefined') return;
    localStorage.removeItem(this.AUDIT_STORAGE_KEY);
  }
}

// Export singleton instance
export const cookieManager = new CookieManager();
export default cookieManager;
